<template>
  <div class="iq-card">
    <div class="iq-card-body">
      <div class="d-flex align-items-center">
        <div class="w-100">
          <h5 class="mb-2 chart-title text-secondary">{{ title }}</h5>
          <div class="d-flex justify-content-between align-items-center">
            <h3 class="counter">{{ value }}<span class="counter-unit">{{ displayUnit }}</span></h3>
          </div>
        </div>
      </div>
      <apexchart :height="chartOptions.chart.height" :type="chartOptions.chart.type" :options="themedChartOptions" :series="series" />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { formatDateTime } from '@/util/date';
import { CHART_TOOLTIP, CHART_AXIS } from '@/util/apiDatetime';
import { formatNum } from '@/util/number';
import { interpolateNulls } from '@/util/summary/series';
import chartThemeMixin from '@/components/widgets/charts/chartThemeMixin';

export default {
  name: 'TotalBunkerChart',
  mixins: [chartThemeMixin],
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    summaryData: { type: Object, required: true },
    unit: { type: Object, required: true },
    mappedSeries: { type: Array, required: true },
  },
  computed: {
    title() {
      return 'Total Bunker';
    },
    displayUnit() {
      return this.unit.bunker === 'Mass'
        ? this.unit.mass.toUpperCase()
        : this.unit.volume.toUpperCase();
    },
    value() {
      if (!this.summaryData?.flowmeter?.summary?.BUNKER_IN) return '0.00';
      const summary = this.summaryData.flowmeter.summary.BUNKER_IN;
      let rawValue = 0;
      if (this.unit.bunker === 'Mass') {
        rawValue = summary._totalMassFlow || 0;
        if (this.unit.mass === 'mt') rawValue *= 0.001;
      } else {
        rawValue = summary._totalVolumeFlow || 0;
        if (this.unit.volume === 'kl') rawValue *= 0.001;
      }
      return formatNum(rawValue, 2);
    },
    series() {
      const dataKey = this.unit.bunker === 'Mass' ? 'massTotal' : 'volumeTotal';
      // eslint-disable-next-line no-nested-ternary
      const conversion = this.unit.bunker === 'Mass'
        ? (this.unit.mass === 'mt' ? 0.001 : 1)
        : (this.unit.volume === 'kl' ? 0.001 : 1);

      const seriesData = this.mappedSeries.length > 60 ? this.mappedSeries.slice(-60) : this.mappedSeries;

      const mappedData = seriesData.map((d) => {
        const rawValue = d.fmData?.BUNKER_IN?.[dataKey];
        if (rawValue == null || !Number.isFinite(Number(rawValue))) {
          return null;
        }
        return Number((Number(rawValue) * conversion).toFixed(2));
      });

      const interpolatedData = interpolateNulls(mappedData);

      return [
        { name: this.title, data: mappedData },
        { name: `${this.title} (Interpolated)`, data: interpolatedData },
      ];
    },
    chartOptions() {
      const seriesData = this.mappedSeries.length > 60 ? this.mappedSeries.slice(-60) : this.mappedSeries;
      const categories = seriesData.map((d) => formatDateTime(d.timestamp * 1000, CHART_TOOLTIP, null, false));

      return {
        chart: {
          height: 80,
          type: 'area',
          sparkline: { enabled: true },
          group: 'sparklines',
          animations: {
            enabled: false, // <--- ANIMASI DIMATIKAN
          },
        },
        dataLabels: { enabled: false },
        stroke: {
          width: 3,
          curve: 'smooth',
          dashArray: [0, 4],
        },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 },
        },
        colors: ['#0084ff', 'rgba(0, 132, 255, 0.4)'],
        xaxis: {
          type: 'datetime',
          categories,
        },
        tooltip: {
          enabled: true,
          custom({
            series, seriesIndex, dataPointIndex, w,
          }) {
            if (seriesIndex === 1 || series[seriesIndex][dataPointIndex] === null) {
              return '';
            }
            const seriesName = w.globals.seriesNames[seriesIndex];
            const value = series[seriesIndex][dataPointIndex];
            const color = w.globals.colors[seriesIndex];
            const category = w.config.xaxis.categories[dataPointIndex];
            const title = formatDateTime(category, CHART_AXIS, CHART_TOOLTIP);

            return `<div class="apexcharts-tooltip apexcharts-theme-light" style="font-family: inherit; font-size: 12px;"><div class="apexcharts-tooltip-title" style="padding: 6px 10px;">${title}</div><div class="apexcharts-tooltip-series-group apexcharts-active" style="padding: 6px 10px; display: flex; align-items: center;"><span class="apexcharts-tooltip-marker" style="background-color: ${color}; width: 12px; height: 12px; margin-right: 10px; border-radius: 50%;"></span><div class="apexcharts-tooltip-text"><div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">${seriesName}: </span><span class="apexcharts-tooltip-text-y-value">${value}</span></div></div></div></div>`;
          },
        },
      };
    },
  },
  methods: {
  },
};
</script>

<style scoped>
.counter {
  font-size: 1em;
}

.counter-unit {
  font-size: 0.7em;
  font-weight: 500;
  margin-left: 3px;
}
</style>
